import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ChartConfiguration, ChartData, Plugin, PointElement } from 'chart.js';
import { Subscription } from 'rxjs';
import { ThermometerReading, TempDataPoint } from '../version.service';
import { updatedAgo as fmtAgo, fmtDayMonthYear, humidityColor, tempColor } from '../shared/format.utils';
import { CHART_RANGES, CHART_RANGE_MS, ChartRange, chartPalette, coveringRange, setpointColor, uiLocale } from '../shared/chart.utils';
import { DeepLinkService } from '../shared/deep-link.service';
import { knxCompositeName, knxLocationName, knxReadingName } from '../shared/knx-names';
import { ValveOverlayRow, clampValvePct, inkOn, trackOn, valveOverlayPlugin } from './knx-valve-overlay';
import { MOBILE_VIEW_QUERY, PhoneMedia } from './knx-devices.utils';

/**
 * The KNX heating rooms (Gira HomeServer room controllers), shown in the Klima tab: summary tiles, the
 * "Übersicht Ist / Soll" overview, the rooms by floor (a list in the mobile view, sparkline cards from
 * 768 px) and each room's popup chart (+ the admin's KNX details).
 *
 * Deep link (DeepLinkService): the room popup is the view "klima/room/<deviceId>" (also the id of its
 * chart: zoom + legend keys unprefixed), the rooms' range "klima.range", the rooms in humidity mode
 * "klima.humidity=<id>,<id>". Old KNX-tab links (knx/room/<id>, knx.range, knx.humidity) are migrated
 * by the service.
 */
const LINK_TAB = 'klima';
/** Deep-link list key of the rooms whose chart shows the humidity. */
const HUMIDITY_KEY = `${LINK_TAB}.humidity`;

type DeltaState = 'below' | 'ok' | 'above' | 'none';
/** What a room card's sparkline / popup chart shows (humidity only for rooms whose sensor sends it). */
type RoomChartMode = 'temp' | 'humidity';

interface KnxSummary {
  avgTempC:     number;
  avgSetpointC: number | null;
  withSetpoint: number;   // rooms that report a Soll (denominator of below/above)
  below:        number;
  above:        number;
  coldest:      ThermometerReading;
  warmest:      ThermometerReading;
}

/** Bar / swatch colours of the Ist-vs-Soll overview (diverging: blue below, gray within, red above).
 *  Rooms without a Soll get a faint outlined bar (`none` fill + `noneBorder`), so they never read as on target.
 *  `target`: the Soll tick — the orange of every Soll line (setpointColor), never an Ist colour.
 *  `surface`: the card background under the chart (the faint fill's backdrop for the valve labels' contrast,
 *  and the rim that sets the Soll tick off the bar it sits on). */
interface OverviewPalette { below: string; ok: string; above: string; none: string; noneBorder: string; target: string; surface: string; }

/** Ist more than this far from Soll counts as below / above target (cards, tiles and overview). */
const DELTA_TOLERANCE = 0.5;
/** Overview chart: pixels per room row, plus room for the x-axis ticks. */
const ROW_PX = 22;
const AXIS_PX = 34;
/** Overview: rim (card colour) on each side of a Soll tick — the orange tick on a red bar would blend in otherwise. */
const SOLL_RIM_PX = 1;
/** Archive reads per range switch run at most this many at a time (one GET per room). */
const RANGE_CONCURRENCY = 4;
/** Room list row: the sparkline's size (styles.css .dash-row-spark) and the number of time buckets drawn. */
const SPARK_W = 72;
const SPARK_H = 28;
const SPARK_BINS = 48;
/** Room list row: the sparkline's y span is at least this (°C), so sensor noise stays a flat line. */
const SPARK_MIN_SPAN = 1;

/** SVG paths of a room list row's sparkline (Ist line, dashed Soll steps); '' = nothing to draw. */
interface RoomSpark { ist: string; soll: string; }
const NO_SPARK: RoomSpark = Object.freeze({ ist: '', soll: '' });

/** Shared "no points" value: a stable reference, so a room without data does not re-render its chart on every pass. */
const NO_POINTS: TempDataPoint[] = Object.freeze([]) as unknown as TempDataPoint[];

/** One archived value of the room's mapped valve (/api/history "knxga-16-…" series, 0..100 %). */
interface ValvePoint { ts: number; pct: number; }
/**
 * Valve archive of one room for the current range, read with agg=time (time-weighted buckets: a PWM
 * valve's buckets are already its duty). `to`: end of the read — later chart points take their own
 * valvePct (live, the current mapping). `pwm`: a 2-point / PWM output (the reading says so, or the raw
 * series only switches between 0 and 100 %) — shown as the time-weighted duty over the last 30 min,
 * like the backend's live valvePct, instead of a 0 / 100 comb.
 */
interface ValveArchive { series: string; at: number; to: number; pts: ValvePoint[]; pwm: boolean; }

/** Valve archive reads in parallel (one GET per room with a mapped valve). */
const VALVE_CONCURRENCY = 3;
/** Tag: a failed / empty valve archive read is retried this often. */
const VALVE_DAY_REFRESH_MS = 10 * 60 * 1000;
/** A held valve value older than this (or 3 archive buckets, whichever is longer) is a gap, not a value. */
const VALVE_STALE_MS = 30 * 60 * 1000;
/** Window of the PWM duty (time-weighted share of "open"). */
const PWM_WINDOW_MS = 30 * 60 * 1000;
const NO_VALVE: ValvePoint[] = Object.freeze([]) as unknown as ValvePoint[];

/**
 * GET /api/admin/knx/rooms (admin session only; KnxHeatingRooms.view) — per KNX heating room the heating
 * actuator channel that controls it, the channel's group addresses with the last value heard on the bus
 * and its sender, the HomeServer's data points and the known anomalies (read tolerantly, everything but
 * `deviceId` is optional):
 *
 *   { asOf?, table?: { readAt?, source?, product? }, homeServerIa?,
 *     rooms: [{ deviceId, name?, floor?, room?, actuator?: { ia?, channel?, product?, pwm? },
 *               homeServer?: { functionUid?, currentDp?, setPointDp?, current?, setPoint? },
 *               effectiveSetpoint?,
 *               gas?: [{ role, ga, obj?, direction? ('in' | 'status' | 'out'), writable?, flags?, dpt?, probable?,
 *                        name? (ETS book), value?, unit?, text?, mode?, raw?, ts?, sender?, senderLabel?,
 *                        otherSenders?: [{ ia, label? }] }],
 *               valve?: { ga, status?, roomDeviceId?, pct?, ts?, pwm? } | null,
 *               humidity?: { ga, basis?, sender? } | null,
 *               notes?: [{ key, params? }] }] }
 *
 * Notes (machine keys, worded here): watchdog {ga, istGa, istAgeMin?}, sharedSetpoint {dp?, otherRoom,
 * otherDeviceId, ga, ownGa}, setpointSharedWith {dp?, otherRoom, otherDeviceId} (its set-point lands on this
 * channel), setpointDpShared {dp?, otherRoom, otherDeviceId} (same data point, no claim about the channel),
 * hsWritesStatus {ga | landingUnknown, statusGa, shiftGa, baseGa, hsWrites?}, noValve {actuator, channel}.
 *
 * Read-only: the backend answers from what it already holds (no bus telegram, no HomeServer request).
 */
interface RawKnxSender { ia?: string; label?: string | null; product?: string | null; name?: string | null; }
interface RawKnxGa {
  role?: string; ga?: string; obj?: number | null; direction?: string; writable?: boolean | null; flags?: string | null;
  dpt?: string | null; probable?: boolean | null; name?: string | null;
  value?: number | string | boolean | null; unit?: string | null; text?: string | null; mode?: string | null; raw?: string | null;
  ts?: number | null; sender?: string | RawKnxSender | null; senderLabel?: string | null; senderProduct?: string | null;
  otherSenders?: RawKnxSender[] | null;
}
interface RawKnxRoom {
  deviceId?: string; name?: string | null; floor?: string | null; room?: string | null;
  actuator?: { ia?: string | null; channel?: string | number | null; product?: string | null; pwm?: boolean | null } | null;
  homeServer?: { functionUid?: string | null; currentDp?: string | null; setPointDp?: string | null;
                 current?: number | null; setPoint?: number | null } | null;
  effectiveSetpoint?: number | { value?: number | null; ts?: number | null } | null;
  gas?: RawKnxGa[] | null;
  valve?: { ga?: string | null; status?: string | null; pct?: number | null; ts?: number | null; pwm?: boolean | null } | null;
  humidity?: { ga?: string | null; basis?: string | null; sender?: string | null } | null;
  notes?: { key?: string; params?: Record<string, unknown> | null }[] | null;
}
interface RawKnxRooms {
  asOf?: number | string | null;
  table?: { readAt?: string | null; source?: string | null; product?: string | null } | null;
  homeServerIa?: string | null;
  rooms?: RawKnxRoom[] | null;
}

/** One group object of a room's controller channel (view model; labels are i18n keys). */
interface AdminGaRow {
  key:       string;
  role:      string;          // i18n key (KNX_ADMIN_ROLE_…) or the backend's role as is
  roleHint:  string;          // i18n key of its tooltip, '' none
  probable:  boolean;         // the role is a well-founded guess (the base set-point input)
  badge:     string;          // valve row: i18n key of the mapping status ("aus Aktor"), else ''
  ga:        string;
  gaTitle:   string;          // "ETS name · DPT 9.001 · C-WTU"
  dir:       string;          // i18n key of in / status / out, or the backend's value; '' unknown
  writable:  boolean | null;
  value:     string;          // last value heard, formatted ('' none)
  warn:      boolean;         // valve at the 49.8 % emergency value (the backend's watchdog note)
  ts:        number | null;
  sender:    string;          // the value's sender IA (else the GA's dominant one)
  /** "+1": other devices that sent values to this GA (the HomeServer on a set-point status, …). */
  others:    string;
  senderTitle: string;        // what is known about the sender, plus the other senders
}
/** KNX details of one room (view model, rebuilt per read and on a language switch). */
interface AdminRoomView {
  actuatorIa: string;
  channel:    string;
  product:    string;
  pwm:        boolean;
  /** HomeServer data points: i18n key + value ("Funktion f518", "Soll-DP a510"). */
  hs:         { key: string; v: string }[];
  hsSet:      number | null;
  effSet:     number | null;
  /** The room sensor's humidity GA and sender ('' none). */
  humidityGa: string;
  humiditySender: string;
  humidityLikely: boolean;
  rows:       AdminGaRow[];
  notes:      string[];
  /** GA tags of the expanded chart's legend. */
  istGa:      string | null;
  valveGa:    string | null;
}

/** Admin KNX details: read once on showing the rooms, then at most this often while a room chart is open. */
const ADMIN_POLL_MS = 60_000;
/** Order of a heating controller channel's objects: inputs, status objects, status text, valve; others after. */
const ADMIN_ROLE_ORDER = ['ist', 'base', 'shift', 'mode', 'setpoint', 'shiftStatus', 'modeStatus', 'text', 'valve'];
/** DPT 5.001 0x7F: the actuator's emergency control value after 30 min without a room temperature. */
const WATCHDOG_PCT = 49.8;
/** DPT 20.102 text values the backend may send instead of the number. */
const HVAC_MODE_NAMES: Readonly<Record<string, number>> = {
  auto: 0, comfort: 1, standby: 2, economy: 3, night: 3, frost: 4, protection: 4, building_protection: 4,
};

@Component({
  selector: 'app-knx-rooms',
  templateUrl: './knx-rooms.component.html',
  styles: [`
    .knx-tile-label { font-size: .68rem; letter-spacing: .04em; text-transform: uppercase; color: var(--bs-secondary-color); line-height: 1.2; }
    .knx-tile-value { font-size: 1.35rem; font-weight: 600; line-height: 1.15; font-variant-numeric: tabular-nums; }
    .knx-tile-sub   { font-size: .72rem; color: var(--bs-secondary-color); line-height: 1.25; }
    .knx-legend     { font-size: .7rem; column-gap: .75rem; row-gap: .15rem; }
    .knx-swatch     { display: inline-block; width: .7rem; height: .7rem; border-radius: 2px; margin-right: .3rem; vertical-align: -.08rem; }
    .knx-tick       { display: inline-block; width: 3px; height: .85rem; border-radius: 1px; margin: 0 .35rem 0 .2rem; vertical-align: -.15rem; }
    .knx-hum       { flex-shrink: 0; font-size: .72rem; font-weight: 600; line-height: 1.1; font-variant-numeric: tabular-nums; white-space: nowrap; }
    .knx-mode-btn   { width: 36px; height: 44px; padding: 0; opacity: .7; margin-right: -4px; }
    /* Heating valve of a room card: "🔧 45 %", a thin gauge in the chart's valve colour, "vermutet" while only learned */
    .knx-valve       { display: flex; align-items: center; gap: .3rem; font-size: .68rem; line-height: 1.3; min-width: 0; }
    .knx-valve-num   { flex-shrink: 0; font-variant-numeric: tabular-nums; white-space: nowrap; color: var(--bs-secondary-color); }
    .knx-valve-gauge { flex: 1 1 auto; min-width: 1.2rem; height: 4px; border-radius: 2px; background: var(--bs-secondary-bg); overflow: hidden; }
    .knx-valve-gauge > span { display: block; height: 100%; border-radius: 2px; background: #0ca678; }
    .knx-valve-badge { flex-shrink: 0; font-size: .56rem; font-weight: 600; line-height: 1; padding: .14rem .3rem; border-radius: 999px;
                       border: 1px dashed var(--bs-border-color); color: var(--bs-secondary-color); white-space: nowrap; }
    /* Admin: KNX details under the expanded room chart (actuator channel, group addresses, notes) */
    .knx-adm       { border-top: 1px solid var(--bs-border-color-translucent); padding-top: .5rem; font-size: .72rem; line-height: 1.35; min-width: 0; }
    .knx-adm-head  { display: flex; flex-wrap: wrap; align-items: baseline; gap: .1rem .45rem; margin-bottom: .2rem; }
    .knx-adm-badge { font-size: .56rem; font-weight: 600; line-height: 1; padding: .14rem .35rem; border-radius: 999px;
                     background: var(--bs-secondary-bg); color: var(--bs-secondary-color); }
    .knx-adm-sub   { font-size: .68rem; color: var(--bs-secondary-color); overflow-wrap: anywhere; }
    .knx-adm-set   { margin: .2rem 0 .35rem; display: flex; flex-wrap: wrap; column-gap: .35rem; }
    .knx-adm-table { border: 1px solid var(--bs-border-color); border-radius: .4rem; overflow-x: auto; }
    .knx-adm-row   { display: grid; grid-template-columns: minmax(8rem, 1.4fr) 4.6rem minmax(6.2rem, auto) minmax(6rem, 1fr) minmax(4.2rem, .7fr);
                     gap: .1rem .6rem; align-items: baseline; padding: .25rem .5rem; min-width: 0; }
    .knx-adm-row + .knx-adm-row { border-top: 1px solid var(--bs-border-color-translucent); }
    .knx-adm-th    { font-size: .62rem; font-weight: 600; color: var(--bs-secondary-color); background: var(--bs-tertiary-bg); }
    .knx-adm-row > span { min-width: 0; overflow-wrap: anywhere; }
    .knx-adm-val   { text-align: right; font-variant-numeric: tabular-nums; }
    .knx-adm-age   { font-size: .62rem; color: var(--bs-secondary-color); white-space: nowrap; }
    .knx-adm-dir   { display: flex; flex-wrap: wrap; align-items: baseline; gap: .25rem; }
    .knx-adm-flag  { font-size: .56rem; font-weight: 600; line-height: 1; padding: .12rem .3rem; border-radius: .25rem; white-space: nowrap;
                     background: var(--bs-success-bg-subtle); color: var(--bs-success-text-emphasis); }
    .knx-adm-flag.off { background: transparent; color: var(--bs-secondary-color); border: 1px dashed var(--bs-border-color); }
    .knx-adm-st    { font-size: .56rem; font-weight: 600; margin-left: .3rem; padding: .1rem .3rem; border-radius: 999px; white-space: nowrap;
                     color: var(--bs-success-text-emphasis); border: 1px solid var(--bs-success-border-subtle); }
    .knx-adm-mono  { font-family: var(--bs-font-monospace); font-size: .68rem; }
    .knx-adm-notes { list-style: none; margin: .4rem 0 0; padding: .3rem .55rem; border-left: 3px solid var(--bs-warning-border-subtle);
                     background: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis); border-radius: .3rem; }
    .knx-adm-notes > li + li { margin-top: .2rem; }
    /* Phones and popups too narrow for the five columns (≈ 610 px with a classic scrollbar): each object
       in two lines — name + value, then GA / direction / sender */
    @media (max-width: 639.98px) {
      .knx-adm-row { grid-template-columns: auto minmax(0, 1fr) auto; grid-template-areas: "role role val" "ga dir snd"; }
      .knx-adm-th  { display: none; }
      .knx-adm-c-role { grid-area: role; } .knx-adm-c-ga { grid-area: ga; } .knx-adm-c-dir { grid-area: dir; }
      .knx-adm-c-val  { grid-area: val; }  .knx-adm-c-snd { grid-area: snd; text-align: right; }
    }
    /* Room popup: Ist / Soll / valve lines beside the range buttons */
    .knx-pop-info { font-size: .7rem; line-height: 1.4; }
    .knx-pop-meta { font-size: .6rem; }
    /* Room list of the mobile view (styles.css pattern (d)): state bar in the overview's colours */
    .knx-row-state  { display: block; width: 4px; height: 32px; border-radius: 2px; }
    .knx-row-state-none { box-shadow: inset 0 0 0 1px var(--bs-secondary-color); opacity: .6; }
    .knx-row-sub    { display: flex; flex-wrap: wrap; align-items: baseline; column-gap: .6rem; }
    .knx-row-sub > span { white-space: nowrap; }
    .knx-row-vals   { display: flex; flex-direction: column; align-items: flex-end; min-width: 3.1rem; }
    .knx-row-temp   { font-size: 1.0625rem; font-weight: 600; line-height: 1.15; }
    .knx-row-hum    { font-size: .6875rem; font-weight: 600; line-height: 1.2; }
    /* non-scaling strokes: the sparkline stretches to a wider box from 576 px (preserveAspectRatio="none") */
    .knx-spark-ist, .knx-spark-soll { fill: none; stroke-width: 1.5; stroke-linejoin: round; stroke-linecap: round; vector-effect: non-scaling-stroke; }
    .knx-spark-soll { stroke-width: 1.25; stroke-dasharray: 3 2.5; stroke-linecap: butt; opacity: .9; }
    @media (min-width: 576px) {
      .knx-tile-value { font-size: 1.6rem; }
    }
    /* Mobile view (< 768 px, landscape phones included): no text under 11 px */
    @media (max-width: 767.98px) {
      .knx-tile-label, .knx-valve-badge, .knx-pop-meta { font-size: .6875rem; }
      .knx-valve-badge { padding: .1rem .35rem; }
      .knx-pop-info { font-size: .8125rem; }
    }
    /* Phones: the range controls take a full-width row (segmented control) */
    @media (max-width: 575.98px) {
      .knx-range, .knx-pop-ctl { flex: 1 0 100%; margin-left: 0 !important; }
      .knx-range > .dash-seg, .knx-pop-ctl > .dash-seg { flex: 1 1 auto; width: auto; }
      .knx-pop-ctl .knx-mode-btn { width: var(--dash-touch); height: var(--dash-touch); margin-right: 0; }
      .knx-pop-info { flex: 1 0 100%; }
    }
    /* Mobile view from 576 px: the range buttons stay at the end of their row, in the phones' touch size
       (styles.css sizes .dash-seg only below 576 px) */
    @media (min-width: 576px) and (max-width: 767.98px) {
      .dash-seg > .btn { min-height: var(--dash-touch-sm); padding: 4px 12px; font-size: .8125rem; }
      .dash-seg > .btn-outline-secondary { --bs-btn-color: var(--bs-secondary-color); }
      /* wide rows: a longer sparkline (the paths stretch, see .knx-spark-ist); the coldest / warmest room wraps
         like on phones (styles.css .m-wrap acts below 576 px only) */
      .knx-room-list .dash-row-spark { width: 144px; }
      .knx-tile-sub.m-wrap { white-space: normal; overflow: visible; text-overflow: clip; }
    }
  `],
})
export class KnxRoomsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() thermometers:       ThermometerReading[]                    = [];
  @Input() thermometerHistory: {[deviceId: string]: TempDataPoint[]}  = {};
  @Input() knxFlash            = false;
  @Input() knxUpdatedAt:       number | null                           = null;
  @Input() liveDotOn           = true;
  @Input() nowTick             = Date.now();
  @Input() darkMode            = false;
  /** Admin session headers (X-Session-Token) while the dashboard holds an admin token, else null: the
   *  expanded room charts then also show the room's KNX details (group addresses, actuator channel, notes). */
  @Input() adminAuth: (() => Record<string, string>) | null = null;
  /** /api/admin/knx/rooms answered 401: the held token is stale (the dashboard drops it). */
  @Output() adminUnauthorized = new EventEmitter<void>();

  // ── KNX heating rooms (Gira HomeServer) in backend floor/room order ──
  rooms:     ThermometerReading[] = [];
  knxFloors: { floor: string; rooms: ThermometerReading[] }[] = [];
  summary:   KnxSummary | null = null;

  // Overview chart: horizontal Ist bars coloured by delta, Soll as a vertical tick marker.
  overviewData:    ChartData<'bar' | 'line', (number | null)[], string> = { labels: [], datasets: [] };
  overviewOptions: ChartConfiguration<'bar' | 'line'>['options'] = {};
  overviewHeight = 0;
  pal: OverviewPalette = KnxRoomsComponent.palette(false);
  private overviewOptionsKey = '';
  /** Faint rule between floors, so the E1…E5 groups read at a glance. */
  readonly overviewPlugins: Plugin<'bar' | 'line'>[] = [{
    id: 'knxFloorRule',
    beforeDatasetsDraw: (chart) => {
      const y = chart.scales['y'];
      const area = chart.chartArea;
      if (!y || !area || this.rooms.length < 2) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.strokeStyle = chartPalette(this.darkMode).gridColor;
      ctx.lineWidth = 1;
      for (let i = 1; i < this.rooms.length; i++) {
        if ((this.rooms[i].floor ?? '') === (this.rooms[i - 1].floor ?? '')) continue;
        const py = Math.round((y.getPixelForValue(i - 1) + y.getPixelForValue(i)) / 2) + 0.5;
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.lineTo(area.right, py);
        ctx.stroke();
      }
      ctx.restore();
    },
  },
  {
    // Under each Soll tick (dataset 1) a rim in the card's colour: the orange tick stays apart from the red /
    // blue bar it sits on (the hovered tick's larger radius included)
    id: 'knxSollRim',
    beforeDatasetDraw: (chart, args) => {
      if (args.index !== 1) return;
      const ctx = chart.ctx;
      ctx.save();
      ctx.strokeStyle = this.pal.surface;
      ctx.lineCap = 'butt';
      for (const el of args.meta.data as unknown as PointElement[]) {
        const { x, y, skip } = el.getProps(['x', 'y', 'skip'], true) as { x: number; y: number; skip: boolean };
        if (skip || !Number.isFinite(x) || !Number.isFinite(y)) continue;
        const r = el.options.radius + SOLL_RIM_PX;
        ctx.lineWidth = el.options.borderWidth + 2 * SOLL_RIM_PX;
        ctx.beginPath();
        ctx.moveTo(x, y - r);
        ctx.lineTo(x, y + r);
        ctx.stroke();
      }
      ctx.restore();
    },
  },
  // Each room's valve position on its bar: a thin stripe along the bottom and "45 %" beside the Soll tick
  valveOverlayPlugin({
    datasetIndex: 0,
    rows: () => this.overviewValves,
    outsideInk: () => chartPalette(this.darkMode).textColor,
  })];
  /** Index-aligned with the overview's bars: valve, Soll and the label / track colours of the bar's fill. */
  private overviewValves: ValveOverlayRow[] = [];
  /** At least one room has a current valve value: the legend shows the valve stripe. */
  overviewHasValves = false;

  // ── Room chart range (cards, list rows and popups alike): Tag = live 24h, rest = archive ──
  range: ChartRange = 'day';
  rangeLoading = false;
  /** Archive points of the current range only (cleared on every switch); NO_POINTS marks a failed / empty read. */
  private rangePoints: {[deviceId: string]: TempDataPoint[]} = {};
  /** Rooms of the current range still waiting for a request slot / waiting for their response. */
  private rangeQueue: string[] = [];
  private rangeInFlight = new Set<string>();
  /** Aborts the current range's requests on a switch; its identity also drops responses of an old range. */
  private rangeAbort: AbortController | null = null;
  /** Range buttons; labels are i18n keys (RANGE_DAY …). */
  readonly chartRanges = CHART_RANGES;

  /** Rooms switched to the humidity chart (only offered while the room reports a humidity); in the link as "klima.humidity". */
  private roomMode: {[deviceId: string]: RoomChartMode} = {};

  // ── Heating valve per room (public: confirmed, verified, book, likely — only likely is badged) ──
  /** At least one room has a mapped valve: the legend shows the valve swatch. */
  hasValves = false;
  /** Archived valve history of the current range per room (cleared on a range switch). */
  private valveArch: {[deviceId: string]: ValveArchive} = {};
  private valveQueue: string[] = [];
  private valveInFlight = new Set<string>();
  /** Replaced on every range switch: aborts the old reads and drops their late responses. */
  private valveAbort = new AbortController();
  /** Index-aligned valve values per room, recomputed only when the points or the archive change. */
  private valveCache = new Map<string, { pts: TempDataPoint[]; arch: ValveArchive | undefined; out: (number | null)[] | null }>();

  // ── Admin: KNX details per room (only with an admin session; public visitors never request them) ──
  /** Details per room deviceId (empty without an admin session / before the first read). */
  private adminRooms = new Map<string, AdminRoomView>();
  private adminRaw: RawKnxRooms | null = null;
  /** The backend's "as of" of the last read. */
  adminAsOf: number | null = null;
  /** When the channel table was read from the actuators ("14.10.2026"), '' unknown. */
  adminTableDate = '';
  /** A read has finished (successful or not) since the session showed up. */
  adminLoaded = false;
  /** Error of the last read ("HTTP 404"), null after a successful one. */
  adminError: string | null = null;
  /** Start of the last read (client clock): reads are at least ADMIN_POLL_MS apart. */
  private adminFetchedAt = 0;
  /** The running read; its identity also drops the response of a session that ended meanwhile. */
  private adminAbort: AbortController | null = null;
  /** Room cards whose chart is open (popup), as "floor|deviceId": the details are refreshed while at least one is. */
  private openCharts = new Set<string>();

  /** Mobile view (< 768 px, where the shell shows its mobile tab bar): the rooms are a compact list (one row per
   *  room) instead of the sparkline cards. */
  readonly listView = new PhoneMedia(undefined, MOBILE_VIEW_QUERY);
  /** Room list rows: sparkline paths per points array (+ the window they were drawn for). */
  private sparkCache = new WeakMap<TempDataPoint[], { windowMs: number; spark: RoomSpark }>();

  // Cached per points array: the card footers ask for min/max on every change-detection pass.
  private minMaxCache = new WeakMap<TempDataPoint[], { min: number; max: number; minHum: number | null; maxHum: number | null }>();
  private numFmt: { lang: string; fmt: Intl.NumberFormat } | null = null;

  readonly tempColor = tempColor;
  readonly humidityColor = humidityColor;

  /** UI language: argument of the pure `knxName` pipe (English room / floor names unless German). */
  lang: string;
  private langSub: Subscription;
  private linkSub?: Subscription;

  constructor(readonly translate: TranslateService, private readonly link: DeepLinkService) {
    this.lang = translate.currentLang || translate.defaultLang || 'en';
    // Room names and the Ist / Soll dataset labels of the overview are built in TS: rebuild on a switch.
    this.langSub = translate.onLangChange.subscribe((e: LangChangeEvent) => {
      this.lang = e.lang;
      this.buildOverview();
      if (this.adminRaw) this.buildAdmin();   // values, mode names and notes are formatted in TS
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // nowTick / liveDotOn tick every second: only rebuild on new readings or a theme switch.
    if (changes['thermometers']) {
      this.rooms = this.thermometers.filter(t => t.source === 'knx' && Number.isFinite(t.tempC));
      // A card that went away (room dropped, or moved to another floor) took its open popup with it
      // without a close event: it no longer counts as open
      const cards = new Set(this.rooms.map(t => KnxRoomsComponent.chartKey(t)));
      for (const k of this.openCharts) if (!cards.has(k)) this.openCharts.delete(k);
      const floors = new Map<string, ThermometerReading[]>();
      for (const t of this.rooms) {
        const floor = t.floor ?? '';
        if (!floors.has(floor)) floors.set(floor, []);
        floors.get(floor)!.push(t);
      }
      this.knxFloors = [...floors].map(([floor, rooms]) => ({ floor, rooms }));
      this.summary = this.buildSummary(this.rooms);
      this.hasValves = this.rooms.some(t => KnxRoomsComponent.hasValve(t));
      // A room that showed up after Woche/Monat/Jahr was picked still needs its archive read.
      if (this.range !== 'day') {
        this.fetchRange(this.rooms.map(t => t.deviceId).filter(id =>
          !(id in this.rangePoints) && !this.rangeInFlight.has(id) && !this.rangeQueue.includes(id)));
      }
    }
    // New / re-mapped valves, and (Tag) whether the live points already cover the window.
    if (changes['thermometers'] || changes['thermometerHistory']) this.syncValves();
    if (changes['thermometers'] || changes['darkMode']) this.buildOverview();
    // Admin session gone: drop the details at once (and never show them to the next visitor)
    if (changes['adminAuth'] && !this.adminAuth) this.resetAdmin();
    if (changes['adminAuth'] || changes['nowTick']) this.pollAdmin();
  }

  ngOnInit(): void {
    // The link's range and room chart mode (after the first inputs), again on back / forward / an edited link
    this.applyLink();
    this.linkSub = this.link.external$.subscribe(() => this.applyLink());
  }

  ngOnDestroy(): void {
    this.langSub.unsubscribe();
    this.linkSub?.unsubscribe();
    this.listView.destroy();
    this.cancelRange();
    this.valveAbort.abort();
    this.adminAbort?.abort();
  }

  // ── Deep link ───────────────────────────────────────────────────────────────
  /** View path of a room's popup (and the id of its linked chart): "klima/room/knx-f470". */
  roomPath(deviceId: string): string {
    return `${LINK_TAB}/room/${deviceId}`;
  }

  /**
   * Applies the link (on init and when the browser navigates): the rooms in humidity mode
   * ("klima.humidity=<id>,<id>"; cards and popups alike), the rooms' range ("klima.range") — widened to cover a
   * zoom window linked for the open room chart, so a link opened days later still shows it. The room popup
   * opens itself (app-popup-card [linkId]). Only a link into the Klima tab (or one carrying the "klima." key in
   * question) says anything about these: on desktop, where every section is shown, another tab's link leaves
   * them as they are (a tab switch drops the "klima." keys, so their absence there does not mean the default).
   */
  private applyLink(): void {
    const link = this.link;
    const inTab = link.tab === LINK_TAB;
    const room = inTab ? link.childOf(`${LINK_TAB}/room`) : null;
    if (inTab || link.get(HUMIDITY_KEY) != null) {
      this.roomMode = {};
      for (const id of link.getList(HUMIDITY_KEY)) this.roomMode[id] = 'humidity';
      // A room popup's unprefixed "mode=humidity" (the old popups' key) is read too, and moved to the list
      if (room && link.get('mode') === 'humidity') {
        this.roomMode[room] = 'humidity';
        link.set('mode', null);
        this.linkHumidity();
      }
    }

    if (!inTab && link.range(LINK_TAB) == null) return;
    const linked = link.range(LINK_TAB) ?? 'day';
    const range = coveringRange(linked, room ? link.zoom(this.roomPath(room)) : null);
    if (range !== linked) link.setRange(LINK_TAB, range);
    if (range !== this.range) this.setRange(range);
  }

  // Readings arrive as fresh objects every frame (two pollers): keep the cards (and an open
  // expanded chart) instead of re-creating them.
  trackDevice = (_: number, t: ThermometerReading) => t.deviceId;
  trackFloor  = (_: number, g: { floor: string }) => g.floor;

  // ── Formatting ──────────────────────────────────────────────────────────────
  /** Locale-aware number with fixed decimals ("18,4" in German, "18.4" otherwise). */
  num(v: number, digits = 1): string {
    const lang = uiLocale();
    if (digits !== 1) return v.toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    if (this.numFmt?.lang !== lang) {
      this.numFmt = { lang, fmt: new Intl.NumberFormat(lang, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) };
    }
    return this.numFmt.fmt.format(v);
  }

  /** `num` as a stable function reference for the sparklines' axis ticks and tooltips. */
  readonly numFn = (v: number): string => this.num(v);

  /** "E3 Wohnzimmer" / "E3 Living room": floor + room in the UI language, falling back to the backend name. */
  roomLabel(t: ThermometerReading): string {
    return knxReadingName(t, this.lang);
  }

  /** Signed actual − target (Ist − Soll) with one decimal, e.g. "+0,4°" / "−1,2°" / "±0,0°". */
  deltaLabel(delta: number): string {
    const r = Math.round(delta * 10) / 10;
    const sign = r > 0 ? '+' : r < 0 ? '−' : '±';
    return `${sign}${this.num(Math.abs(r))}°`;
  }

  /** Blue when the room is more than 0.5° below target, red when more than 0.5° above. */
  deltaColor(delta: number): string {
    if (delta < -DELTA_TOLERANCE) return 'text-primary';
    if (delta > DELTA_TOLERANCE)  return 'text-danger';
    return 'text-muted';
  }

  private deltaState(t: ThermometerReading): DeltaState {
    if (t.setpointC == null) return 'none';
    const d = t.tempC - t.setpointC;
    return d < -DELTA_TOLERANCE ? 'below' : d > DELTA_TOLERANCE ? 'above' : 'ok';
  }

  /** "Ventil 45 %" (+ " · vermutet") for the overview tooltip; null without a current valve value. */
  private valveLine(t: ThermometerReading): string | null {
    if (t.valvePct == null) return null;
    const line = `${this.label('KNX_VALVE', 'Ventil')} ${this.num(t.valvePct, 0)} %`;
    return t.valveStatus === 'likely' ? `${line} · ${this.label('KNX_VALVE_LIKELY', 'vermutet')}` : line;
  }

  /** "Ist 18,4° · Soll 21,0° (−2,6°)" — Soll part only when the room reports one. */
  private istSollLine(t: ThermometerReading): string {
    const ist = `${this.label('TEMP_ACTUAL', 'Ist')} ${this.num(t.tempC)}°`;
    if (t.setpointC == null) return ist;
    return `${ist} · ${this.label('TEMP_SETPOINT', 'Soll')} ${this.num(t.setpointC)}° (${this.deltaLabel(t.tempC - t.setpointC)})`;
  }

  /** Translated label, falling back to `fallback` while the language file is still loading. */
  private label(key: string, fallback: string): string {
    const t = this.translate.instant(key);
    return t && t !== key ? t : fallback;
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  // ── Summary tiles ───────────────────────────────────────────────────────────
  private buildSummary(rooms: ThermometerReading[]): KnxSummary | null {
    if (!rooms.length) return null;
    let sum = 0, spSum = 0, withSetpoint = 0, below = 0, above = 0;
    let coldest = rooms[0], warmest = rooms[0];
    for (const t of rooms) {
      sum += t.tempC;
      if (t.tempC < coldest.tempC) coldest = t;
      if (t.tempC > warmest.tempC) warmest = t;
      if (t.setpointC == null) continue;   // no Soll: excluded from the below/above counts
      withSetpoint++;
      spSum += t.setpointC;
      const state = this.deltaState(t);
      if (state === 'below') below++;
      else if (state === 'above') above++;
    }
    return {
      avgTempC: sum / rooms.length,
      avgSetpointC: withSetpoint ? spSum / withSetpoint : null,
      withSetpoint, below, above, coldest, warmest,
    };
  }

  // ── Overview chart ──────────────────────────────────────────────────────────
  private static palette(dark: boolean): OverviewPalette {
    // Checked for colour-vision deficiency separation against the light / dark card surface.
    return dark
      ? { below: '#4f93f7', ok: '#5c636a', above: '#e8606b', none: 'rgba(173,181,189,.16)', noneBorder: '#adb5bd', target: setpointColor(true), surface: '#212529' }
      : { below: '#0d6efd', ok: '#8a939c', above: '#dc3545', none: 'rgba(138,147,156,.16)', noneBorder: '#6c757d', target: setpointColor(false), surface: '#fff' };
  }

  private buildOverview(): void {
    const rooms = this.rooms;
    this.pal = KnxRoomsComponent.palette(this.darkMode);
    this.overviewHeight = rooms.length * ROW_PX + AXIS_PX;
    this.overviewHasValves = rooms.some(t => clampValvePct(t.valvePct) != null);
    if (!rooms.length) { this.overviewData = { labels: [], datasets: [] }; this.overviewValves = []; return; }

    const values = rooms.flatMap(t => t.setpointC != null ? [t.tempC, t.setpointC] : [t.tempC]);
    const xMin = Math.floor(Math.min(...values)) - 1;
    const xMax = Math.ceil(Math.max(...values)) + 1;
    const pal = this.pal;
    const stateColor: Record<DeltaState, string> = { below: pal.below, ok: pal.ok, above: pal.above, none: pal.none };
    const states = rooms.map(t => this.deltaState(t));
    // Valve stripe + number on each bar: white or dark label ink, whichever contrasts more with the bar's fill
    const look = {} as Record<DeltaState, { ink: string; track: string }>;
    for (const s of Object.keys(stateColor) as DeltaState[]) {
      look[s] = { ink: inkOn(stateColor[s], pal.surface), track: trackOn(stateColor[s], pal.surface) };
    }
    this.overviewValves = rooms.map((t, i) => ({
      pct: t.valvePct, likely: t.valveStatus === 'likely', setpoint: t.setpointC ?? null, ...look[states[i]],
    }));

    this.overviewData = {
      labels: rooms.map(t => this.roomLabel(t)),
      datasets: [
        {
          type: 'bar',
          indexAxis: 'y',
          label: this.label('TEMP_ACTUAL', 'Ist'),
          data: rooms.map(t => t.tempC),
          backgroundColor: states.map(s => stateColor[s]),
          // Only rooms without a Soll get an outline (faint fill + border).
          borderColor: states.map(s => s === 'none' ? pal.noneBorder : 'transparent'),
          borderWidth: states.map(s => s === 'none' ? 1 : 0),
          borderRadius: 4,
          borderSkipped: 'start',
          barPercentage: 0.9,
          categoryPercentage: 0.8,
          maxBarThickness: 16,
          order: 1,
        },
        {
          // Soll: no line, just a vertical tick per room in the Soll orange, drawn on top of the bars.
          type: 'line',
          indexAxis: 'y',
          label: this.label('TEMP_SETPOINT', 'Soll'),
          data: rooms.map(t => t.setpointC ?? null),
          showLine: false,
          pointStyle: 'line',
          pointRotation: 90,
          pointRadius: 8,
          pointHoverRadius: 9,
          pointBorderWidth: 3,
          pointHoverBorderWidth: 3,
          borderColor: pal.target,
          backgroundColor: pal.target,
          // Set explicitly: in a bar-based mixed chart the point colours do not fall back to borderColor.
          pointBorderColor: pal.target,
          pointBackgroundColor: pal.target,
          pointHoverBorderColor: pal.target,
          pointHoverBackgroundColor: pal.target,
          order: 0,
        },
      ],
    };

    // Options only change with the theme or the axis bounds, not with every reading. The theme is
    // part of the key, so a toggle while no rooms were shown is still picked up when they return.
    const optionsKey = `${xMin}|${xMax}|${this.darkMode}`;
    if (optionsKey !== this.overviewOptionsKey) {
      this.overviewOptionsKey = optionsKey;
      this.overviewOptions = this.buildOverviewOptions(xMin, xMax);
    }
  }

  private buildOverviewOptions(xMin: number, xMax: number): ChartConfiguration<'bar' | 'line'>['options'] {
    const { textColor, gridColor } = chartPalette(this.darkMode);
    return {
      indexAxis: 'y',
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      // Hovering anywhere on a room's row shows that room.
      interaction: { mode: 'index', axis: 'y', intersect: false },
      layout: { padding: { top: 2, right: 10, bottom: 0, left: 0 } },
      plugins: {
        legend: { display: false },
        // The sparkline's global time crosshair makes no sense on a category chart.
        ...({ crosshairLine: false } as any),
        tooltip: {
          // One line per room (the bar), carrying Ist, Soll and the delta.
          filter: (item) => item.datasetIndex === 0,
          displayColors: true,
          callbacks: {
            title: (items) => {
              const t = this.rooms[items[0]?.dataIndex ?? -1];
              return t ? this.roomLabel(t) : '';
            },
            label: (item) => {
              const t = this.rooms[item.dataIndex];
              if (!t) return '';
              const valve = this.valveLine(t);
              return valve ? [` ${this.istSollLine(t)}`, ` ${valve}`] : ` ${this.istSollLine(t)}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'linear',
          min: xMin,
          max: xMax,
          ticks: {
            color: textColor,
            font: { size: 10 },
            precision: 0,
            maxRotation: 0,
            // Only round step values: the non-round min/max would crowd next to them on phones.
            includeBounds: false,
            callback: (v) => `${v}°`,
          },
          grid: { color: gridColor },
        },
        y: {
          type: 'category',
          ticks: {
            color: textColor,
            font: { size: 11 },
            autoSkip: false,
            callback: (v) => {
              const l = this.overviewData.labels?.[v as number] ?? '';
              return l.length > 22 ? l.slice(0, 21) + '…' : l;
            },
          },
          grid: { display: false },
        },
      },
    };
  }

  // ── Room cards: sparklines + expanded chart ─────────────────────────────────
  /** Translated label of the current range (re-read every change detection, so it follows a language switch). */
  get rangeLabel(): string {
    const key = this.chartRanges.find(r => r.key === this.range)?.label;
    return key ? this.translate.instant(key) : '';
  }

  get windowMs(): number {
    return CHART_RANGE_MS[this.range];
  }

  /** Swatch colour of the dashed Soll line in the sparklines (the shared Soll colour, as sparkline-chart draws it). */
  get setpointLineColor(): string {
    return setpointColor(this.darkMode);
  }

  sparklinePoints(deviceId: string): TempDataPoint[] {
    return this.thermometerHistory[deviceId] ?? NO_POINTS;
  }

  /** Points for a room's chart: live for day, fetched archive for week/month/year. */
  expandedPoints(deviceId: string): TempDataPoint[] {
    if (this.range === 'day') return this.sparklinePoints(deviceId);
    return this.rangePoints[deviceId] ?? NO_POINTS;
  }

  /** Range buttons (room cards and room popup): the range for every room chart, kept in the link ("klima.range"). */
  selectRange(range: ChartRange): void {
    const changed = range !== this.range;
    this.setRange(range);
    this.link.setRange(LINK_TAB, range);
    // The open room chart's zoom window belonged to the old range (its chart rebuilds unzoomed)
    const room = this.link.childOf(`${LINK_TAB}/room`);
    if (changed && room) this.link.setZoom(this.roomPath(room), null);
  }

  /** Range for every KNX room chart (fetches each KNX room for week/month/year). */
  setRange(range: ChartRange): void {
    this.range = range;
    // Drop the previous range's data and requests: no stale points under the new label.
    this.cancelRange();
    this.resetValves();
    if (range !== 'day') {   // Tag: the live history covers 24h
      this.rangeAbort = new AbortController();
      this.fetchRange(this.rooms.map(t => t.deviceId));
    }
    // The mapped valves' archive of the new range, alongside the room series.
    this.syncValves();
  }

  private cancelRange(): void {
    this.rangeAbort?.abort();
    this.rangeAbort = null;
    this.rangePoints = {};
    this.rangeQueue = [];
    this.rangeInFlight.clear();
    this.rangeLoading = false;
  }

  /** Queues archive reads of the current range for these rooms (one GET each, RANGE_CONCURRENCY at a time). */
  private fetchRange(deviceIds: string[]): void {
    if (!this.rangeAbort || !deviceIds.length) return;
    this.rangeQueue.push(...deviceIds);
    this.rangeLoading = true;
    this.pumpRange();
  }

  private pumpRange(): void {
    const ctrl = this.rangeAbort;
    if (!ctrl) return;
    while (this.rangeInFlight.size < RANGE_CONCURRENCY && this.rangeQueue.length) {
      const deviceId = this.rangeQueue.shift()!;
      this.rangeInFlight.add(deviceId);
      const to     = Date.now();
      const from   = to - CHART_RANGE_MS[this.range];
      const series = encodeURIComponent('thermo-' + deviceId);
      fetch(`/api/history?series=${series}&from=${from}&to=${to}&buckets=600`, { signal: ctrl.signal })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then((pts: TempDataPoint[] | null) => Array.isArray(pts) && pts.length ? pts : NO_POINTS)
        .catch(() => NO_POINTS)   // failed / aborted: an empty chart, not a retry on every frame
        .then(pts => {
          if (ctrl !== this.rangeAbort) return;   // range switched (or component gone) meanwhile
          this.rangePoints[deviceId] = pts;
          this.rangeInFlight.delete(deviceId);
          this.rangeLoading = this.rangeInFlight.size > 0 || this.rangeQueue.length > 0;
          this.pumpRange();
        });
    }
  }

  // ── Room list rows (mobile view) ────────────────────────────────────────────
  /** Fill of a room row's state bar: the overview's bar colour (below / within / above target, faint without a Soll). */
  rowStateColor(t: ThermometerReading): string {
    return this.pal[this.deltaState(t)];
  }

  /** Line colour of the row sparklines' Ist (the overview's "below" blue, lighter on the dark surface). */
  get sparkIstColor(): string {
    return this.darkMode ? '#4f93f7' : '#0d6efd';
  }

  /** viewBox of the room rows' sparklines (the paths of roomSpark are drawn in it). */
  readonly sparkViewBox = `0 0 ${SPARK_W} ${SPARK_H}`;

  /**
   * A room row's sparkline over the selected range: Ist (bucket means) and the Soll as steps (drawn dashed),
   * as SVG paths in a SPARK_W × SPARK_H box. More than three empty buckets in a row break the line. Cached
   * per points array (a new reading or range brings a new one).
   */
  roomSpark(deviceId: string): RoomSpark {
    const pts = this.expandedPoints(deviceId);
    const windowMs = this.windowMs;
    const hit = this.sparkCache.get(pts);
    if (hit && hit.windowMs === windowMs) return hit.spark;
    const spark = KnxRoomsComponent.sparkPaths(pts, windowMs, Date.now());
    this.sparkCache.set(pts, { windowMs, spark });
    return spark;
  }

  private static sparkPaths(pts: TempDataPoint[], windowMs: number, end: number): RoomSpark {
    if (!pts.length) return NO_SPARK;
    const start = end - windowMs;
    const sum = new Array<number>(SPARK_BINS).fill(0);
    const cnt = new Array<number>(SPARK_BINS).fill(0);
    const soll = new Array<number | null>(SPARK_BINS).fill(null);
    for (const p of pts) {
      if (p.ts < start || !Number.isFinite(p.tempC)) continue;
      const i = Math.min(SPARK_BINS - 1, Math.floor((p.ts - start) / windowMs * SPARK_BINS));
      sum[i] += p.tempC;
      cnt[i]++;
      if (p.setpointC != null && Number.isFinite(p.setpointC)) soll[i] = p.setpointC;
    }
    const ist = sum.map((v, i) => cnt[i] ? v / cnt[i] : null);
    const values = [...ist, ...soll].filter((v): v is number => v != null);
    if (!values.length) return NO_SPARK;
    let lo = Math.min(...values), hi = Math.max(...values);
    if (hi - lo < SPARK_MIN_SPAN) { const mid = (lo + hi) / 2; lo = mid - SPARK_MIN_SPAN / 2; hi = mid + SPARK_MIN_SPAN / 2; }
    const x = (i: number) => ((i + .5) / SPARK_BINS * SPARK_W).toFixed(1);
    const y = (v: number) => (SPARK_H - 2 - (v - lo) / (hi - lo) * (SPARK_H - 4)).toFixed(1);
    const path = (vals: (number | null)[], steps: boolean) => {
      let d = '', last = -Infinity;
      vals.forEach((v, i) => {
        if (v == null) return;
        d += i - last > 4 ? `M${x(i)} ${y(v)}` : steps ? `H${x(i)}V${y(v)}` : `L${x(i)} ${y(v)}`;
        last = i;
      });
      return d;
    };
    return { ist: path(ist, false), soll: path(soll, true) };
  }

  /** Min / max Ist (and humidity, null without any) of the room's currently shown points; null while there are none. */
  sparklineMinMax(deviceId: string): { min: number; max: number; minHum: number | null; maxHum: number | null } | null {
    const pts = this.expandedPoints(deviceId);
    if (!pts?.length) return null;
    let mm = this.minMaxCache.get(pts);
    if (!mm) {
      let min = Infinity, max = -Infinity, minHum = Infinity, maxHum = -Infinity;
      for (const p of pts) {
        if (p.tempC < min) min = p.tempC;
        if (p.tempC > max) max = p.tempC;
        if (p.humidity != null) {
          if (p.humidity < minHum) minHum = p.humidity;
          if (p.humidity > maxHum) maxHum = p.humidity;
        }
      }
      mm = { min, max, minHum: Number.isFinite(minHum) ? minHum : null, maxHum: Number.isFinite(maxHum) ? maxHum : null };
      this.minMaxCache.set(pts, mm);
    }
    return mm;
  }

  // ── Heating valve (Stellgröße) per room ─────────────────────────────────────
  /** The reading names a mapped valve (series and / or a current value). */
  private static hasValve(t: ThermometerReading): boolean {
    return !!t.valveSeries || t.valvePct != null;
  }

  /** Width of the card's valve gauge, 0..100. */
  valveWidth(pct: number): number {
    return Math.max(0, Math.min(100, pct));
  }

  /**
   * The room's valve position index-aligned with its chart points (null where unknown), or null
   * without any. The archive of the room's current valve (valveSeries) wins wherever it reaches: a
   * point's own valvePct does not say which valve it came from, so after a re-mapping it still holds
   * the old valve's values. Own values only after the archive read (the live tail) and while there is
   * no archive (not read yet / failed). Cached per points array and archive, so the chart only
   * re-renders when either changes.
   */
  valveFor(t: ThermometerReading): (number | null)[] | null {
    if (!KnxRoomsComponent.hasValve(t)) return null;
    const pts  = this.expandedPoints(t.deviceId);
    const a    = this.valveArch[t.deviceId];
    const arch = a && a.series === t.valveSeries ? a : undefined;
    const hit  = this.valveCache.get(t.deviceId);
    if (hit && hit.pts === pts && hit.arch === arch) return hit.out;
    const out = this.alignValve(pts, arch);
    this.valveCache.set(t.deviceId, { pts, arch, out });
    return out;
  }

  private alignValve(pts: TempDataPoint[], arch: ValveArchive | undefined): (number | null)[] | null {
    if (!pts.length) return null;
    // Archive buckets get wider with the range (600 per window): hold a value across ~3 of them.
    const stale = Math.max(VALVE_STALE_MS, 3 * this.windowMs / 600);
    const src = arch?.pts.length ? arch : null;
    let any = false;
    const out = pts.map(p => {
      const own = typeof p.valvePct === 'number' && Number.isFinite(p.valvePct) ? this.valveWidth(p.valvePct) : null;
      const v = !src || (p.ts > src.to && own != null) ? own
        : src.pwm ? KnxRoomsComponent.dutyAt(src.pts, p.ts, stale) : KnxRoomsComponent.holdAt(src.pts, p.ts, stale);
      if (v != null) any = true;
      return v;
    });
    return any ? out : null;
  }

  /** Index of the last archive point at or before `ts` (−1: none). */
  private static lastAtOrBefore(pts: ValvePoint[], ts: number): number {
    let lo = 0, hi = pts.length - 1, idx = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (pts[mid].ts <= ts) { idx = mid; lo = mid + 1; } else hi = mid - 1;
    }
    return idx;
  }

  /** The archived value in effect at `ts` (sample-and-hold), null before the first / after a gap. */
  private static holdAt(pts: ValvePoint[], ts: number, stale: number): number | null {
    const i = KnxRoomsComponent.lastAtOrBefore(pts, ts);
    return i >= 0 && ts - pts[i].ts <= stale ? pts[i].pct : null;
  }

  /** PWM channel: time-weighted mean of the held 0 / 100 values over the 30 min before `ts`. */
  private static dutyAt(pts: ValvePoint[], ts: number, stale: number): number | null {
    const last = KnxRoomsComponent.lastAtOrBefore(pts, ts);
    if (last < 0 || ts - pts[last].ts > stale) return null;
    const from = ts - PWM_WINDOW_MS;
    let i = Math.max(0, KnxRoomsComponent.lastAtOrBefore(pts, from));
    let sum = 0, covered = 0;
    for (; i <= last; i++) {
      const a = Math.max(from, pts[i].ts);
      const b = i < last ? Math.min(ts, pts[i + 1].ts) : ts;
      if (b <= a) continue;
      sum += pts[i].pct * (b - a);
      covered += b - a;
    }
    return covered > 0 ? sum / covered : pts[last].pct;
  }

  /** Archive JSON ({ts, value}) → sorted points 0..100; flags a series that only switches 0 ↔ 100 as PWM. */
  private static valvePoints(raw: unknown): { pts: ValvePoint[]; pwm: boolean } {
    if (!Array.isArray(raw)) return { pts: NO_VALVE, pwm: false };
    const pts: ValvePoint[] = [];
    for (const p of raw) {
      const v = typeof p?.value === 'boolean' ? (p.value ? 100 : 0) : p?.value;
      if (typeof p?.ts === 'number' && typeof v === 'number' && Number.isFinite(v)) {
        pts.push({ ts: p.ts, pct: Math.max(0, Math.min(100, v)) });
      }
    }
    pts.sort((a, b) => a.ts - b.ts);
    // At least a few on/off switches and nothing in between: a 2-point / PWM output (raw, not bucketed).
    let flips = 0, binary = true;
    for (let i = 0; i < pts.length && binary; i++) {
      binary = pts[i].pct <= 0.5 || pts[i].pct >= 99.5;
      if (i && (pts[i].pct >= 50) !== (pts[i - 1].pct >= 50)) flips++;
    }
    return { pts: pts.length ? pts : NO_VALVE, pwm: binary && flips >= 4 };
  }

  /** New range: drop the valve archive and its pending reads (the caller queues the new range's). */
  private resetValves(): void {
    this.valveAbort.abort();
    this.valveAbort = new AbortController();
    this.valveArch = {};
    this.valveQueue = [];
    this.valveInFlight.clear();
    this.valveCache.clear();
  }

  /**
   * Queues the valve archive reads the shown charts need: once per range and valve series (a room
   * re-mapped to another valve reads the new one; on Tag a failed / empty read is retried every 10
   * min) — on Woche / Monat / Jahr alongside the room series. Only for rooms whose reading names a
   * (public) valve series.
   */
  private syncValves(): void {
    const now = Date.now();
    for (const t of this.rooms) {
      const id = t.deviceId, series = t.valveSeries;
      if (!series) { delete this.valveArch[id]; continue; }
      if (this.valveInFlight.has(id) || this.valveQueue.includes(id)) continue;
      const a = this.valveArch[id];
      if (this.range === 'day') {
        if (!this.sparklinePoints(id).length) continue;   // no chart yet
        if (a && a.series === series && (a.pts.length || now - a.at < VALVE_DAY_REFRESH_MS)) continue;
      } else if (a && a.series === series) {
        continue;
      }
      this.valveQueue.push(id);
    }
    this.pumpValves();
  }

  private pumpValves(): void {
    const ctrl  = this.valveAbort;
    const range = this.range;
    while (this.valveInFlight.size < VALVE_CONCURRENCY && this.valveQueue.length) {
      const id = this.valveQueue.shift()!;
      const room = this.rooms.find(t => t.deviceId === id);
      const series = room?.valveSeries;
      if (!series) continue;
      const pwmValve = room?.valvePwm === true;
      this.valveInFlight.add(id);
      const to   = Date.now();
      const from = to - CHART_RANGE_MS[range];
      // agg=time: time-weighted buckets — a PWM valve's week / month / year is its duty, not the mean of its 0 / 100 telegrams
      fetch(`/api/history?series=${encodeURIComponent(series)}&from=${from}&to=${to}&buckets=600&agg=time`, { signal: ctrl.signal })
        .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
        .then(raw => KnxRoomsComponent.valvePoints(raw))
        .catch(() => ({ pts: NO_VALVE, pwm: false }))   // failed: the points' own values (Tag retries after the refresh interval)
        .then(({ pts, pwm }) => {
          if (ctrl !== this.valveAbort) return;   // range switched (or component gone) meanwhile
          this.valveInFlight.delete(id);
          this.valveArch[id] = { series, at: Date.now(), to, pts, pwm: pwm || pwmValve };
          this.pumpValves();
        });
    }
  }

  // ── Room humidity (KNX sensors that also send relative humidity) ────────────
  /** The room's chart: humidity only while chosen and the room still reports one. */
  chartMode(t: ThermometerReading): RoomChartMode {
    return t.humidity != null && this.roomMode[t.deviceId] === 'humidity' ? 'humidity' : 'temp';
  }

  /** Card footer min / max in the chart's quantity: "18,4°" or "48 %" (blank without humidity points). */
  mmLabel(t: ThermometerReading, mm: { min: number; max: number; minHum: number | null; maxHum: number | null }, which: 'min' | 'max'): string {
    if (this.chartMode(t) === 'temp') return `${this.num(which === 'min' ? mm.min : mm.max)}°`;
    const h = which === 'min' ? mm.minHum : mm.maxHum;
    return h != null ? `${this.num(h, 0)} %` : '';
  }

  toggleChartMode(event: Event, t: ThermometerReading): void {
    event.stopPropagation();   // the card itself opens the popup on a click
    this.roomMode[t.deviceId] = this.chartMode(t) === 'humidity' ? 'temp' : 'humidity';
    this.linkHumidity();
  }

  /** The rooms in humidity mode go into the link ("klima.humidity=<id>,<id>"; temperature is the default). The
   *  key is prefixed, so it survives opening / closing a room popup, like "klima.range". */
  private linkHumidity(): void {
    this.link.setList(HUMIDITY_KEY, Object.keys(this.roomMode).filter(id => this.roomMode[id] === 'humidity').sort());
  }

  // ── Admin: KNX details of a room (GET /api/admin/knx/rooms, read-only) ──────
  trackAdminGa = (_: number, g: AdminGaRow) => g.key;

  /** The room's KNX details, null without an admin session or before / without a read. */
  adminRoom(deviceId: string): AdminRoomView | null {
    return this.adminRooms.get(deviceId) ?? null;
  }

  /** Admin legend tag of the expanded chart's Ist line ("7/5/0"). */
  adminIstGa(t: ThermometerReading): string | null {
    return this.adminRooms.get(t.deviceId)?.istGa ?? null;
  }

  /** Admin legend tag of the valve area: the GA of the series the chart draws ("knxga-16-5-0"), else the details' valve GA. */
  adminValveGa(t: ThermometerReading): string | null {
    const k = this.adminRooms.get(t.deviceId);
    if (!k) return null;
    const m = /^knxga-(\d+)-(\d+)-(\d+)$/.exec(t.valveSeries ?? '');
    return m ? `${m[1]}/${m[2]}/${m[3]}` : k.valveGa;
  }

  /** HomeServer set-point: its data point's value from the details, else the reading's Soll (the same data point). */
  adminHsSet(k: AdminRoomView, t: ThermometerReading): number | null {
    return k.hsSet ?? t.setpointC ?? null;
  }

  /** The actuator controls to another set-point than the HomeServer shows. */
  adminSetDiffers(k: AdminRoomView, t: ThermometerReading): boolean {
    const hs = this.adminHsSet(k, t);
    return hs != null && k.effSet != null && Math.abs(hs - k.effSet) >= 0.05;
  }

  /** "14:32:05" in the UI language. */
  clockTime(ts: number): string {
    return new Date(ts).toLocaleTimeString(uiLocale());
  }

  /** "14.10.26, 14:32:05": tooltip of a value's age. */
  fullTime(ts: number): string {
    return new Date(ts).toLocaleString(uiLocale(), { dateStyle: 'short', timeStyle: 'medium' });
  }

  /**
   * A room's chart (popup) opened / closed: an open one keeps its details fresh. The link follows the
   * popup's view on opening (its chart mode is in "klima.humidity", which survives the view change); a
   * close goes back to "#klima" by app-popup-card itself (closeView).
   */
  chartOpened(t: ThermometerReading, open: boolean): void {
    const path = this.roomPath(t.deviceId);
    if (open) {
      this.openCharts.add(KnxRoomsComponent.chartKey(t));
      this.link.openView(path);
      this.pollAdmin();
    } else {
      this.openCharts.delete(KnxRoomsComponent.chartKey(t));
    }
  }

  /** A room card's identity in the floor groups: "E5|knx-f518". */
  private static chartKey(t: ThermometerReading): string {
    return `${t.floor ?? ''}|${t.deviceId}`;
  }

  /**
   * Reads the details when due: only with an admin session (the rooms are shown) and while the
   * browser tab is visible — once on showing it, afterwards only while a room chart is open, and at most
   * every ADMIN_POLL_MS (a failed read waits as long, so a missing endpoint is not hammered).
   */
  private pollAdmin(): void {
    if (!this.adminAuth || this.adminAbort) return;
    if (document.visibilityState === 'hidden') return;
    if (this.adminLoaded && !this.openCharts.size) return;
    if (Date.now() - this.adminFetchedAt < ADMIN_POLL_MS) return;
    void this.fetchAdmin();
  }

  private async fetchAdmin(): Promise<void> {
    const auth = this.adminAuth;
    if (!auth) return;
    const ctrl = new AbortController();
    this.adminAbort = ctrl;
    this.adminFetchedAt = Date.now();
    let data: RawKnxRooms | RawKnxRoom[] | null = null;
    let error: string | null = null;
    let unauthorized = false;
    try {
      const r = await fetch('/api/admin/knx/rooms', { cache: 'no-store', headers: auth(), signal: ctrl.signal });
      if (r.status === 401) unauthorized = true;
      else if (!r.ok) error = `HTTP ${r.status}`;
      else data = await r.json();
    } catch (e: any) {
      error = e?.message || String(e);
    }
    if (ctrl !== this.adminAbort) return;   // session ended (or component gone) meanwhile
    this.adminAbort = null;
    if (unauthorized) {
      this.resetAdmin();
      this.adminUnauthorized.emit();
      return;
    }
    this.adminLoaded = true;
    this.adminError = error;
    if (data) {
      this.adminRaw = Array.isArray(data) ? { rooms: data } : data;
      this.buildAdmin();
    }
  }

  private resetAdmin(): void {
    this.adminAbort?.abort();
    this.adminAbort = null;
    this.adminRaw = null;
    this.adminRooms = new Map();
    this.adminAsOf = null;
    this.adminTableDate = '';
    this.adminLoaded = false;
    this.adminError = null;
    this.adminFetchedAt = 0;
  }

  private buildAdmin(): void {
    const raw = this.adminRaw;
    const list = Array.isArray(raw?.rooms) ? raw!.rooms! : [];
    const hsIa = typeof raw?.homeServerIa === 'string' ? raw.homeServerIa : '';
    const map = new Map<string, AdminRoomView>();
    for (const r of list) {
      if (r && typeof r.deviceId === 'string' && r.deviceId) map.set(r.deviceId, this.adminView(r, list, hsIa));
    }
    this.adminRooms = map;
    const asOf = raw?.asOf;
    this.adminAsOf = typeof asOf === 'number' ? asOf
      : typeof asOf === 'string' && Number.isFinite(Date.parse(asOf)) ? Date.parse(asOf) : null;
    const readAt = raw?.table?.readAt;
    this.adminTableDate = typeof readAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(readAt) ? fmtDayMonthYear(readAt, this.lang) : readAt ?? '';
  }

  private adminView(r: RawKnxRoom, all: RawKnxRoom[], hsIa: string): AdminRoomView {
    const str = (v: unknown): string => typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
    const fin = (v: unknown): number | null => typeof v === 'number' && Number.isFinite(v) ? v : null;

    // Group objects in the channel's order (inputs, status objects, text, valve); unknown roles after them
    const gas = (Array.isArray(r.gas) ? r.gas : []).filter(g => g && typeof g.ga === 'string' && g.ga);
    const rank = (g: RawKnxGa): number => { const i = ADMIN_ROLE_ORDER.indexOf(g.role ?? ''); return i < 0 ? ADMIN_ROLE_ORDER.length : i; };
    const sorted = gas.map((g, i) => ({ g, i })).sort((a, b) => rank(a.g) - rank(b.g) || a.i - b.i).map(x => x.g);
    const valve = r.valve && typeof r.valve.ga === 'string' && r.valve.ga ? r.valve : null;
    const gaOf = (role: string): string | null => gas.find(g => g.role === role)?.ga ?? null;

    // Notes: the backend's (it only flags the emergency valve value while the actuator's telegram is
    // current); an answer without notes gets the emergency value / missing valve from the GA list (the
    // valve row's own telegram, never a PWM channel's 30-min duty that happens to be 49.8 %)
    const listed = Array.isArray(r.notes);
    const notes = (listed ? r.notes! : []).filter(n => n && typeof n.key === 'string' && n.key);
    const pwm = r.actuator?.pwm === true || valve?.pwm === true;
    const valveRaw = fin(gas.find(g => g.role === 'valve')?.value);
    const watchdog = listed ? notes.some(n => n.key === 'watchdog')
      : !pwm && valveRaw != null && Math.abs(valveRaw - WATCHDOG_PCT) < 0.05;
    if (!listed) {
      if (watchdog) notes.push({ key: 'watchdog' });
      if (r.valve === null && !gaOf('valve')) notes.push({ key: 'noValve' });
    }

    const rows = sorted.map(g => g.role === 'valve'
      ? this.adminRow(g, valve?.status ?? null, watchdog, hsIa) : this.adminRow(g, null, false, hsIa));
    // A valve the GA list does not carry still gets its row
    if (valve && !gas.some(g => g.ga === valve.ga)) {
      rows.push(this.adminRow({ role: 'valve', ga: valve.ga!, direction: 'out', writable: false, dpt: '5.001',
                                value: valve.pct ?? null, ts: valve.ts ?? null }, valve.status ?? null, watchdog, hsIa));
    }

    const hs = r.homeServer ?? null;
    const hsParts: { key: string; v: string }[] = [];
    if (str(hs?.functionUid)) hsParts.push({ key: 'KNX_ADMIN_HS_FUNCTION',    v: str(hs?.functionUid) });
    if (str(hs?.currentDp))   hsParts.push({ key: 'KNX_ADMIN_HS_CURRENT_DP',  v: str(hs?.currentDp) });
    if (str(hs?.setPointDp))  hsParts.push({ key: 'KNX_ADMIN_HS_SETPOINT_DP', v: str(hs?.setPointDp) });

    // Effective set-point: the backend's value, else the last value of the set-point status object
    const eff = r.effectiveSetpoint;
    const effSet = fin(typeof eff === 'object' && eff ? eff.value : eff)
      ?? fin(gas.find(g => g.role === 'setpoint')?.value);

    const channel = r.actuator?.channel;
    const hum = r.humidity;
    return {
      actuatorIa: str(r.actuator?.ia),
      channel:    channel == null ? '' : String(channel),
      product:    str(r.actuator?.product),
      pwm,
      hs:         hsParts,
      hsSet:      fin(hs?.setPoint),
      effSet,
      humidityGa:     str(hum?.ga),
      humiditySender: str(hum?.sender),
      humidityLikely: !!hum && hum.basis !== 'book',
      rows,
      notes:      notes.map(n => this.adminNote(n.key!, n.params ?? {}, r, gaOf, all)),
      istGa:      gaOf('ist'),
      valveGa:    valve?.ga ?? gaOf('valve'),
    };
  }

  private adminRow(g: RawKnxGa, valveStatus: string | null, warn: boolean, hsIa: string): AdminGaRow {
    const role = g.role ?? '';
    const roleKey = role ? `KNX_ADMIN_ROLE_${KnxRoomsComponent.snake(role)}` : '';
    const known = ADMIN_ROLE_ORDER.includes(role);
    const dir = g.direction === 'in' || g.direction === 'status' || g.direction === 'out'
      ? `KNX_ADMIN_DIR_${g.direction.toUpperCase()}` : (g.direction ?? '');
    const value = this.adminValue(g);
    const ts = typeof g.ts === 'number' && Number.isFinite(g.ts) && g.ts > 0 ? g.ts : null;
    const status = valveStatus && ['confirmed', 'verified', 'likely', 'candidate', 'book'].includes(valveStatus) ? valveStatus : '';

    // Sender: "1.3.5" (+ what the line scan / IP nodes / learned role say); the other senders as "+n"
    const s = g.sender;
    const sender = typeof s === 'string' ? s : s?.ia ?? '';
    const label = (ia: string, l: string | null | undefined): string =>
      l ? `${ia} – ${l}` : ia === hsIa && ia ? `${ia} – HomeServer` : ia;
    const own = typeof s === 'object' && s ? s.label ?? s.product ?? s.name : null;
    const others = (Array.isArray(g.otherSenders) ? g.otherSenders : []).filter(o => o && typeof o.ia === 'string' && o.ia);
    const title = [sender ? label(sender, own ?? g.senderLabel ?? g.senderProduct) : ''];
    if (others.length) {
      title.push(this.translate.instant('KNX_ADMIN_OTHER_SENDERS', {
        list: others.map(o => label(o.ia!, o.label ?? o.product ?? o.name)).join(', '),
      }));
    }
    return {
      key:       `${role}|${g.ga}`,
      role:      known ? roleKey : role || '?',
      roleHint:  known ? `${roleKey}_HINT` : '',
      probable:  g.probable === true,
      badge:     status ? `ADMIN_KNX_VALVES_ST_${status.toUpperCase()}` : '',
      ga:        g.ga ?? '',
      gaTitle:   [g.name ?? '', g.dpt ? `DPT ${g.dpt}` : '', g.flags ?? ''].filter(Boolean).join(' · '),
      dir,
      writable:  typeof g.writable === 'boolean' ? g.writable : null,
      value,
      warn,
      ts,
      sender,
      others:    others.length ? `+${others.length}` : '',
      senderTitle: title.filter(Boolean).join('\n'),
    };
  }

  /**
   * The last value heard with its unit: °C (DPT 9.001), signed K for the shift (9.002), % for the valve
   * (5.001, one decimal unless whole — 49,8 % is the emergency value), the HVAC mode by name (20.102) and
   * the status text (16.001) in quotes; '' without a value.
   */
  private adminValue(g: RawKnxGa): string {
    const role = g.role ?? '';
    const dpt = g.dpt ?? '';
    const v = g.value;
    const isMode = role === 'mode' || role === 'modeStatus' || dpt.startsWith('20.');
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (role === 'valve' || dpt.startsWith('5.')) {
        return `${this.num(v, Number.isInteger(v) ? 0 : 1)} %`;
      }
      if (role === 'shift' || role === 'shiftStatus' || dpt === '9.002') {
        const r = Math.round(v * 10) / 10;
        return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${this.num(Math.abs(r))} K`;
      }
      if (isMode) return this.hvacMode(v);
      if (role === 'ist' || role === 'base' || role === 'setpoint' || dpt.startsWith('9.')) {
        return `${this.num(v)} °C`;
      }
      return g.unit ? `${this.num(v)} ${g.unit}` : String(v);
    }
    if (typeof v === 'boolean') return v ? '1' : '0';
    // DPT 9's "invalid" 0x7FFF (a failing sensor): no value, the backend's text is German — worded here
    const dpt9 = dpt.startsWith('9.') || ['ist', 'base', 'shift', 'setpoint', 'shiftStatus'].includes(role);
    if (v == null && dpt9 && String(g.raw ?? '').toLowerCase() === '7fff') return this.translate.instant('KNX_ADMIN_INVALID');
    // A mode by name ("comfort"), the status text, or the backend's own wording
    const mode = isMode ? HVAC_MODE_NAMES[(g.mode ?? (typeof v === 'string' ? v : '')).trim().toLowerCase().replace(/[\s-]+/g, '_')] : undefined;
    if (mode != null) return this.hvacMode(mode);
    const text = typeof v === 'string' ? v : typeof g.text === 'string' ? g.text : null;
    if (text == null) return '';
    const clean = text.replace(/\u0000+$/, '');
    return role === 'text' || dpt.startsWith('16.') ? `„${clean}“` : clean;
  }

  /** "Komfort (1)": DPT 20.102 by name, an unknown code as is. */
  private hvacMode(n: number): string {
    const key = `KNX_ADMIN_MODE_${n}`;
    const name = this.translate.instant(key);
    return name && name !== key ? `${name} (${n})` : String(n);
  }

  /**
   * A backend note ({key, params}) in the UI language: KNX_ADMIN_NOTE_<KEY> with its params (missing GAs
   * filled from the room's own GA list, the other room in the UI's naming), plus a second sentence where
   * the params carry evidence; an unknown key shows as "key (a=b, …)".
   */
  private adminNote(key: string, params: Record<string, unknown>, r: RawKnxRoom,
                    gaOf: (role: string) => string | null, all: RawKnxRoom[]): string {
    const tr = (k: string, params?: object): string => { const t = this.translate.instant(k, params); return t && t !== k ? t : ''; };
    const p: Record<string, string> = {};
    for (const [k, v] of Object.entries(params)) if (v != null) p[k] = String(v);
    let i18nKey = `KNX_ADMIN_NOTE_${KnxRoomsComponent.snake(key)}`;
    let extra = '';
    if (key === 'hsWritesStatus') {
      // A shared set-point data point whose GA the backend could not decide: no GA is claimed
      const unknown = params['landingUnknown'] === true;
      if (!unknown) p['ga'] ??= gaOf('setpoint') ?? '?';
      p['shiftGa'] ??= gaOf('shift') ?? '?';
      p['baseGa'] ??= gaOf('base') ?? '';
      if (unknown) i18nKey += '_UNKNOWN';
      if (p['baseGa']) i18nKey += '_BASE';
      // How many of the HomeServer's writes onto that GA were overheard (only when its address is known)
      const n = Number(p['hsWrites']);
      if (p['hsWrites'] != null && Number.isFinite(n)) {
        extra = tr(n > 0 ? 'KNX_ADMIN_NOTE_HS_WRITES_SEEN' : 'KNX_ADMIN_NOTE_HS_WRITES_NONE', { n, ga: p['ga'] });
      }
    } else if (key === 'sharedSetpoint' || key === 'setpointSharedWith' || key === 'setpointDpShared') {
      // The other room as the UI names it: its deviceId (knx-f515), else a "<floor> <room>" / room name
      const other = p['otherDeviceId'] ?? p['otherRoom'] ?? '';
      const reading = this.rooms.find(t => t.deviceId === other);
      const listed = all.find(x => x.deviceId === other);
      p['otherRoom'] = reading ? this.roomLabel(reading)
        : listed ? knxLocationName(listed.floor, listed.room, this.lang) || other
        : knxCompositeName(p['otherRoom'] ?? other, this.lang);
      p['dp'] ??= r.homeServer?.setPointDp ?? '';
      p['ga'] ??= '?';
      if (key === 'sharedSetpoint') {
        p['ownGa'] ??= gaOf('setpoint') ?? '';
        if (p['ownGa']) i18nKey += '_OWN';
      }
      if (!p['dp']) i18nKey += '_NODP';   // the HomeServer's data point uids are not known (yet)
    } else if (key === 'watchdog') {
      const min = Number(p['istAgeMin']);
      if (p['istAgeMin'] != null && Number.isFinite(min)) {
        const age = min < 120 ? `${Math.round(min)} min` : `${this.num(min / 60, 0)} h`;
        extra = tr('KNX_ADMIN_NOTE_WATCHDOG_IST_AGE', { ga: p['istGa'] ?? gaOf('ist') ?? '?', age });
      }
    }
    const out = tr(i18nKey, p);
    if (out) return extra ? `${out} ${extra}` : out;
    const args = Object.entries(p).map(([k, v]) => `${k}=${v}`).join(', ');
    return args ? `${key} (${args})` : key;
  }

  /** "shiftStatus" → "SHIFT_STATUS" (i18n key part). */
  private static snake(s: string): string {
    return s.replace(/([a-z0-9])([A-Z])/g, '$1_$2').replace(/[^A-Za-z0-9]+/g, '_').toUpperCase();
  }
}
